import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, type Run } from '../api/client';

type Props = {
  run: Run;
  onUpdated: () => void;
};

/** Управляющие действия над прогоном по backend-флагам permissions. */
export function RunAdminActions({ run, onUpdated }: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(run.title);

  const actions = run.actions ?? {
    can_retry: false,
    can_delete: false,
    can_edit: false,
    can_cancel: false,
  };

  async function act(
    key: string,
    fn: () => Promise<void>,
    confirmMsg?: string,
  ) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(key);
    setError(null);
    try {
      await fn();
      onUpdated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function saveTitle() {
    const clean = draftTitle.trim();
    if (!clean) {
      setError('Название не должно быть пустым');
      return;
    }
    await act('edit', async () => {
      await api.put(`/api/runs/${run.id}`, { title: clean });
      setEditing(false);
    });
  }

  const hasActions =
    actions.can_edit ||
    actions.can_retry ||
    actions.can_delete ||
    actions.can_cancel;
  if (!hasActions) return null;

  return (
    <div className="flex flex-wrap gap-2 items-center min-w-[220px]">
      {editing ? (
        <div className="flex flex-wrap gap-2 items-center">
          <input
            className="input h-8 min-w-[180px]"
            value={draftTitle}
            maxLength={160}
            onChange={(e) => setDraftTitle(e.target.value)}
            disabled={busy !== null}
            aria-label="Название прогона"
          />
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={busy !== null}
            onClick={saveTitle}
          >
            {busy === 'edit' ? '…' : 'Сохранить'}
          </button>
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={busy !== null}
            onClick={() => {
              setDraftTitle(run.title);
              setEditing(false);
            }}
          >
            Отмена
          </button>
        </div>
      ) : actions.can_edit ? (
        <button
          type="button"
          className="btn-subtle btn-sm"
          disabled={busy !== null}
          onClick={() => setEditing(true)}
        >
          Изменить
        </button>
      ) : null}
      {actions.can_cancel &&
        (run.status === 'RUNNING' || run.status === 'CREATED') && (
          <button
            type="button"
            className="btn-subtle btn-sm"
            disabled={busy !== null}
            onClick={() =>
              act(
                'cancel',
                () => api.post(`/api/runs/${run.id}/cancel`),
                'Отменить прогон?',
              )
            }
          >
            {busy === 'cancel' ? '…' : 'Отменить'}
          </button>
        )}
      {actions.can_retry ? (
        <button
          type="button"
          className="btn-subtle btn-sm"
          disabled={busy !== null}
          onClick={() =>
            act(
              'retry',
              () => api.post(`/api/runs/${run.id}/retry`),
              'Перезапустить прогон?',
            )
          }
        >
          {busy === 'retry' ? '…' : 'Перезапустить'}
        </button>
      ) : null}
      {actions.can_delete ? (
        <button
          type="button"
          className="btn-subtle btn-sm text-danger"
          disabled={busy !== null}
          onClick={() =>
            act(
              'delete',
              async () => {
                await api.delete(`/api/runs/${run.id}`);
                navigate('/', { replace: true });
              },
              'Удалить прогон и все связанные данные безвозвратно?',
            )
          }
        >
          {busy === 'delete' ? '…' : 'Удалить'}
        </button>
      ) : null}
      {error ? <span className="error-hint">{error}</span> : null}
    </div>
  );
}
